import { Fragment, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { pivotLevels, resolveClose } from './levelPivots';
import { marketDataService } from '../services/marketDataService';
import { usePermissions } from '../hooks/usePermissions';
import { useUpgradeAction } from '../hooks/useUpgradeAction';
import type { SymbolData, CopyType, ExportType } from '../types/marketData';

/** Disclosure markers. Kept as constants so the JSX stays free of escapes. */
const RIGHT = '\u25b8';
const DOWN = '\u25be';

/** The six rungs, supports first, each running outward from the close. */
const LADDER = [
  { key: 'S1', field: 's1' as const, tone: 'text-[10px] uppercase tracking-wider text-bear-red' },
  { key: 'S2', field: 's2' as const, tone: 'text-[10px] uppercase tracking-wider text-bear-red' },
  { key: 'S3', field: 's3' as const, tone: 'text-[10px] uppercase tracking-wider text-bear-red' },
  { key: 'R1', field: 'r1' as const, tone: 'text-[10px] uppercase tracking-wider text-bull-blue' },
  { key: 'R2', field: 'r2' as const, tone: 'text-[10px] uppercase tracking-wider text-bull-blue' },
  { key: 'R3', field: 'r3' as const, tone: 'text-[10px] uppercase tracking-wider text-bull-blue' },
];

/**
 * What each export type is called on screen.
 *
 * ⚠️ The KEYS are the API contract and must not move — `tradingview-v2` is the
 * value the server expects, and it serves the V3 script. The confirmation
 * toast used to print `type.toUpperCase()`, so copying the V3 export announced
 * "Copied TRADINGVIEW-V2!" and looked like the wrong script had been copied.
 */
const EXPORT_LABELS: Record<string, string> = {
  'tradingview-v2': 'TradingView V3',
  tradingview: 'TradingView',
  tos: 'TOS',
  csv: 'CSV',
};

const DEFAULT_SYMBOLS = ['SPX', 'SPY', 'QQQ', 'DIA'];

interface SteamroomLevelsWidgetProps {
  onRemove: () => void;
}

export function SteamroomLevelsWidget({ onRemove }: SteamroomLevelsWidgetProps) {
  const [symbols, setSymbols] = useState<string[]>(DEFAULT_SYMBOLS);
  const [inputValue, setInputValue] = useState(DEFAULT_SYMBOLS.join(','));
  const [copyStatus, setCopyStatus] = useState<string | null>(null);

  const { hasPermission } = usePermissions();
  const hasSteamroomAccess = hasPermission('steamroom');

  const { onUpgrade, isRedirecting } = useUpgradeAction(!hasSteamroomAccess);

  const { data, isLoading, error } = useQuery({
    queryKey: ['steamroomLevels', symbols],
    queryFn: () => marketDataService.fetchComboData(symbols),
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes (data changes once daily)
    // refetchOnWindowFocus: true (default) - Smart: only refetches if stale
  });

  const rows: SymbolData[] = data ?? [];

  /** Symbols whose ladder is open. A set, so several can be compared at once. */
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpanded = (symbol: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(symbol)) next.delete(symbol);
      else next.add(symbol);
      return next;
    });
  };

  /**
   * A price, or a muted dash when there is none.
   *
   * ⚠️ Returns a NODE, not a string, so the placeholder can carry its own
   * colour. It used to return the literal "N/A", which inherited the cell's
   * tone — so an absent put wall rendered red and an absent call wall blue,
   * and a table with no data at all looked like a table full of errors. Red
   * is for failures; absent is grey.
   */
  const fmt = (val: number | null): React.ReactNode => {
    if (val === null || val === undefined) {
      return <span className="text-gray-600">&mdash;</span>;
    }
    return val.toFixed(2);
  };

  const handleSymbolsChange = (value: string) => {
    setInputValue(value);
    const newSymbols = value.split(',').map(s => s.trim().toUpperCase()).filter(s => s);
    if (newSymbols.length > 0) {
      setSymbols(newSymbols);
    }
  };

  const handleCopy = async (type: CopyType) => {
    try {
      await marketDataService.copyToClipboard(type, symbols);
      setCopyStatus(`Copied ${EXPORT_LABELS[type] ?? type}!`);
      setTimeout(() => setCopyStatus(null), 2000);
    } catch (err) {
      setCopyStatus(`Failed to copy ${EXPORT_LABELS[type] ?? type}`);
      setTimeout(() => setCopyStatus(null), 2000);
      console.error('Copy error:', err);
    }
  };

  const handleDownload = (type: ExportType) => {
    try {
      marketDataService.downloadExport(type, symbols);
    } catch (err) {
      console.error('Download error:', err);
    }
  };

  return (
    <div className="h-full flex flex-col bg-dark-surface rounded-lg overflow-hidden relative">
      {/* Title Bar - drag handle only */}
      <div className="widget-drag-handle flex items-center px-3 py-1.5 bg-chrome border-b border-dark-border cursor-move relative z-20">
        <h3 className="text-sm font-bold text-white uppercase tracking-[1.5px]">Fuzion Levels</h3>
      </div>

      {/* Toolbar - NOT draggable */}
      <div className="flex items-center justify-end px-3 py-1 bg-dark-bg/50 border-b border-dark-border relative z-20">
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-white hover:bg-bear-red/20 text-sm px-2 py-1 rounded transition-colors"
          title="Close widget"
        >
          ×
        </button>
      </div>

      {/* Controls */}
      <div className="px-3 py-2 bg-dark-surface border-b border-dark-border space-y-2">
        {/* Symbol Input */}
        <div className="flex items-center gap-2">
          <input
            type="text"
            value={inputValue}
            onChange={(e) => handleSymbolsChange(e.target.value)}
            placeholder="SPX,SPY,QQQ,DIA"
            className="flex-1 px-2 py-1 text-xs bg-dark-surface border border-dark-border rounded text-white placeholder-gray-500 focus:outline-none focus:border-accent-yellow"
          />
          {!hasSteamroomAccess && (
            <button
              onClick={onUpgrade}
              disabled={isRedirecting}
              className="px-3 py-1 text-xs bg-accent-yellow hover:bg-accent-yellow/80 text-dark-bg font-medium rounded transition-colors disabled:opacity-50"
            >
              {isRedirecting ? 'Redirecting...' : 'Upgrade'}
            </button>
          )}
        </div>

        {/* Export Buttons */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* Copy Buttons */}
          {/* ⚠️ The original TradingView export is gone from the UI. It emits
              the superseded script and is no use to anyone now; only the
              export below is current.

              The `'tradingview'` value stays in `ExportType` / `CopyType` and
              on the server — this removes the button, not the route. */}
          <button
            onClick={() => handleCopy('tradingview-v2')}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-dark-surface-light hover:bg-dark-border text-white rounded transition-colors"
            title="Copy TradingView V3"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <span>TradingView V3</span>
          </button>
          <button
            onClick={() => handleCopy('tos')}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-dark-surface-light hover:bg-dark-border text-white rounded transition-colors"
            title="Copy ThinkorSwim"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
            <span>TOS</span>
          </button>

          {/* Download Buttons */}
          <button
            onClick={() => handleDownload('tradingview-v2')}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-dark-surface-light hover:bg-dark-border text-white rounded transition-colors"
            title="Download TradingView V3"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span>TradingView V3</span>
          </button>
          <button
            onClick={() => handleDownload('tos')}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-dark-surface-light hover:bg-dark-border text-white rounded transition-colors"
            title="Download ThinkorSwim"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span>TOS</span>
          </button>
          <button
            onClick={() => handleDownload('csv')}
            className="flex items-center gap-1 px-2 py-1 text-xs bg-dark-surface-light hover:bg-dark-border text-white rounded transition-colors"
            title="Download CSV"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span>CSV</span>
          </button>

          {/* Get Indicator.
              The label and title must match the TradingView publication EXACTLY:
              the href slug is "Steamroom-Levels-V3-Dynamic-IV", so renaming the
              copy alone sends users looking for a script that does not exist.
              Rename this only together with the indicator on TradingView — which
              also changes the slug, so the href moves in the same commit. */}
          <a
            href="https://www.tradingview.com/script/hScdkKQu-Steamroom-Levels-V3-Dynamic-IV/"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-1 px-2 py-1 text-xs bg-accent-yellow/20 hover:bg-accent-yellow/30 text-accent-yellow rounded transition-colors"
            title="Get the Steamroom Levels V3 (Dynamic IV) indicator on TradingView"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
            <span>Get TradingView V3 Indicator</span>
          </a>

          {/* Copy Status */}
          {copyStatus && (
            <span className="text-xs text-bull-blue">{copyStatus}</span>
          )}
        </div>
      </div>

      {/* Table Container */}
      <div className="flex-1 overflow-auto">
        {isLoading && (
          <div className="flex items-center justify-center h-full text-gray-400 text-sm">
            Loading...
          </div>
        )}

        {error && (
          <div className="flex items-center justify-center h-full text-bear-red text-sm">
            Error loading data
          </div>
        )}

        {rows.length > 0 && (
          /* Five columns at rest, the six pivots one click away.
             Thirteen across scrolled horizontally at every dashboard size;
             this keeps what a glance needs — where price is, how wide the band
             is, and the two major walls — and puts the ladder behind a
             disclosure rather than dropping it. Adding tickers costs rows, not
             width, so an arbitrary symbol list stays readable. */
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-dark-surface-light border-b border-dark-border">
              <tr>
                <th className="px-2 py-2 text-left font-semibold text-gray-300">Symbol</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-300">Close</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-300">Implied Move</th>
                {/* Majors and minors both sit in the row. "Wall" is dropped
                    from each heading — four columns repeating it spent width
                    on a word the group already establishes. */}
                <th className="px-2 py-2 text-right font-semibold text-gray-300">Call Major</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-300">Call Minor</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-300">Put Major</th>
                <th className="px-2 py-2 text-right font-semibold text-gray-300">Put Minor</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item: SymbolData) => {
                /* Derived, not fetched — see `levelPivots`. The close comes
                   from OHLC, falling back to the sigma midpoint when OHLC is
                   null, which is the state the feed is usually in. */
                const move = item.expected_move?.expected_move ?? null;
                const close = resolveClose(item.ohlc?.close, item.expected_move ?? null);
                const pivots = pivotLevels(close, move);
                const open = expanded.has(item.symbol);

                return (
                  <Fragment key={item.symbol}>
                    <tr
                      onClick={() => pivots && toggleExpanded(item.symbol)}
                      aria-expanded={open}
                      className={pivots
                        ? 'border-b border-dark-border transition-colors cursor-pointer hover:bg-dark-surface-light'
                        : 'border-b border-dark-border transition-colors'}
                      title={pivots ? 'Show support and resistance levels' : undefined}
                    >
                      <td className="px-2 py-2 font-numeric font-semibold text-accent-yellow">
                        <span className="inline-flex items-center gap-1">
                          {/* Only rows with a ladder to show get a marker — a
                              chevron on a row that cannot open is a dead
                              control. */}
                          <span className="w-2 text-gray-500">
                            {pivots ? (open ? DOWN : RIGHT) : ''}
                          </span>
                          {item.symbol}
                        </span>
                      </td>
                      <td className="px-2 py-2 text-right font-numeric text-white">{fmt(close)}</td>
                                            {/* ⚠️ Rendered with a sign. This is the half-width the
                          ladder steps in — 15.50 means 15.50 either side of
                          the close, not a 15.50 range — and without the ± it
                          sits among prices and reads as another level. */}
                      <td className="px-2 py-2 text-right font-numeric text-white">
                        {move == null ? fmt(null) : `±${move.toFixed(2)}`}
                      </td>
                      <td className="px-2 py-2 text-right font-numeric text-bull-blue">{fmt(item.gex_levels?.call_wall_major ?? null)}</td>
                      {/* ⚠️ Minors are NOT dimmed. Reducing their opacity made
                          them look like stale or partial data; the label is
                          what says minor. */}
                      <td className="px-2 py-2 text-right font-numeric text-bull-blue">{fmt(item.gex_levels?.call_wall_minor ?? null)}</td>
                      <td className="px-2 py-2 text-right font-numeric text-bear-red">{fmt(item.gex_levels?.put_wall_major ?? null)}</td>
                      <td className="px-2 py-2 text-right font-numeric text-bear-red">{fmt(item.gex_levels?.put_wall_minor ?? null)}</td>
                    </tr>

                    {open && pivots && (
                      <tr className="border-b border-dark-border bg-dark-bg/40">
                        <td colSpan={7} className="px-3 py-2">
                          {/* Named here rather than in the header row: these
                              six ARE the pivot points, and the column above is
                              the distance between them, not one of them. */}
                          <div className="text-[10px] uppercase tracking-wider text-gray-400 mb-1.5">
                            Pivot Points
                          </div>

                          {/* Supports on one line, resistances on the next,
                              each running outward from the close. */}
                          <div className="grid grid-cols-3 gap-x-4 gap-y-1 max-w-md">
                            {LADDER.map((rung) => (
                              <div key={rung.key} className="flex items-baseline justify-between gap-2">
                                <span className={rung.tone}>{rung.key}</span>
                                <span className="font-numeric text-white">{fmt(pivots[rung.field])}</span>
                              </div>
                            ))}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
